import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StoreHeader } from './StoreHeader';
import { StoreFooter } from './StoreFooter';
import { ProductDialog } from './ProductDialog';
import { CartSheet } from './CartSheet';
import { useProducts } from '@/features/products/queries';
import { useCart } from '@/features/cart/store';

/**
 * Chrome shared by every storefront page: header, cart sheet, product dialog
 * and footer. Pages own their own <main> so each keeps a single landmark.
 *
 * The product dialog is URL-driven (?product=slug), so a product link can be
 * shared and the back button closes it.
 */
export function StorefrontShell({
  children,
  floatingHeader = false,
}: {
  children: ReactNode;
  floatingHeader?: boolean;
}) {
  const { data: products } = useProducts();
  const [params, setParams] = useSearchParams();
  const [cartOpen, setCartOpen] = useState(false);

  const reconcile = useCart((state) => state.reconcile);
  const slug = params.get('product');

  const catalog = useMemo(
    () => new Map((products ?? []).map((product) => [product.id, product])),
    [products],
  );

  // Persisted carts survive deploys that remove or deactivate a product.
  useEffect(() => {
    if (!products) return;
    reconcile(
      new Set(products.map((product) => product.id)),
      new Map(products.map((product) => [product.id, product.sizes])),
    );
  }, [products, reconcile]);

  const active = slug ? (products?.find((product) => product.slug === slug) ?? null) : null;

  const closeProduct = (open: boolean) => {
    if (open) return;
    const next = new URLSearchParams(params);
    next.delete('product');
    setParams(next, { replace: true });
  };

  return (
    <>
      <StoreHeader onOpenCart={() => setCartOpen(true)} floating={floatingHeader} />
      {children}
      <StoreFooter />

      <ProductDialog
        key={active?.id ?? 'none'}
        product={active}
        open={Boolean(active)}
        onOpenChange={closeProduct}
        onAdded={() => setCartOpen(true)}
      />

      <CartSheet open={cartOpen} onOpenChange={setCartOpen} catalog={catalog} />
    </>
  );
}
